/** Bounded deterministic primitives. No wall clock or ambient randomness enters replay. */
export const GAME_MOVE_LIMIT = 600;
// Symbolic colour identities only; the design-token owner supplies visual values.
export const GAME_PALETTE = Object.freeze([0, 1, 2, 3, 4, 5]);

export function random(state, size) {
  state.rng = (Math.imul(state.rng, 1664525) + 1013904223) >>> 0;
  return (state.rng >>> 8) % size;
}

export function shuffled(state, values) {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i--) {
    const j = random(state, i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export function adjacent(a, b, columns) {
  return (
    Math.abs(Math.floor(a / columns) - Math.floor(b / columns)) +
      Math.abs((a % columns) - (b % columns)) ===
    1
  );
}

export function neighbors(index, columns, length) {
  return [index - columns, index - 1, index + 1, index + columns].filter(
    (i) => i >= 0 && i < length && adjacent(index, i, columns),
  );
}

export function reject(message = 'That move is not available.') {
  const error = new RangeError(message);
  error.code = 'invalid_game_move';
  throw error;
}

export function cell(index, label, extra = {}) {
  return { label: String(label), move: { type: 'cell', index }, ...extra };
}

export function option(index, label) {
  return { label, move: { type: 'option', index } };
}

/** Swap one orthogonal tile into the blank on an already validated permutation.
 * Caller validates index/dimensions; default3 retains legacy ranked behavior.
 * Reject a non-neighbor before mutation and return actual solved ordering. */
export function slide(board, index, columns = 3) {
  const blank = board.indexOf(0);
  if (!adjacent(blank, index, columns)) reject('Choose a tile beside the empty space.');
  [board[blank], board[index]] = [board[index], board[blank]];
  return board.every((v, i) => v === (i + 1) % board.length);
}

export function gravity(state, board, columns, colours) {
  for (let column = 0; column < columns; column++) {
    const values = board.filter((value, i) => i % columns === column && value !== null);
    while (values.length < board.length / columns) values.unshift(random(state, colours));
    values.forEach((value, row) => {
      board[row * columns + column] = value;
    });
  }
}
